import { useCallback, useEffect, useState } from 'react';
import {
  huurdagen,
  maakApparaatoverzicht,
  type Apparaatregel,
  type Apparaatoverzicht,
} from '@qc/core';
import type { Evenement } from '@qc/db';
import { supabase } from '../supabase.js';

export interface ApparaatMetHuur extends Apparaatregel {
  kraamdeelnameId: string | null;
  huurdagen: number;
}

export interface Apparatenbeeld {
  laden: boolean;
  fout: string | null;
  apparaten: ApparaatMetHuur[];
  overzicht: Apparaatoverzicht;
  kramen: { id: string; naam: string }[];
  ververs: () => Promise<void>;
  geefUit: (apparaatId: string, kraamdeelnameId: string) => Promise<void>;
  neemTerug: (apparaatId: string) => Promise<void>;
  zetDefect: (apparaatId: string, defect: boolean) => Promise<void>;
}

export function useApparaten(evenement: Evenement): Apparatenbeeld {
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState<string | null>(null);
  const [apparaten, setApparaten] = useState<ApparaatMetHuur[]>([]);
  const [kramen, setKramen] = useState<{ id: string; naam: string }[]>([]);

  const ververs = useCallback(async () => {
    const { data: deelnames } = await supabase
      .from('kraamdeelnames')
      .select('id, kraam_id')
      .eq('evenement_id', evenement.id);

    const deelnameLijst = deelnames ?? [];
    const { data: kraamrijen } = await supabase
      .from('kramen')
      .select('id, naam')
      .in(
        'id',
        deelnameLijst.length > 0
          ? deelnameLijst.map((deelname) => deelname.kraam_id)
          : ['00000000-0000-0000-0000-000000000000'],
      );

    const naamPerKraam = new Map((kraamrijen ?? []).map((kraam) => [kraam.id, kraam.naam]));
    const naamPerDeelname = new Map(
      deelnameLijst.map((deelname) => [
        deelname.id,
        naamPerKraam.get(deelname.kraam_id) ?? 'Kraam',
      ]),
    );
    setKramen(
      deelnameLijst.map((deelname) => ({
        id: deelname.id,
        naam: naamPerDeelname.get(deelname.id) ?? 'Kraam',
      })),
    );

    const { data, error } = await supabase
      .from('apparaten')
      .select('*')
      .order('soort')
      .order('serienummer');

    if (error) {
      setFout('De apparatuur kon niet worden opgehaald.');
      setLaden(false);
      return;
    }

    const nu = new Date();
    setApparaten(
      (data ?? []).map((apparaat) => {
        const uitgifte = { uitgegevenOp: apparaat.uitgegeven_op, retourOp: apparaat.retour_op };
        return {
          id: apparaat.id,
          soort: apparaat.soort,
          serienummer: apparaat.serienummer,
          status: apparaat.status,
          kraamdeelnameId: apparaat.kraamdeelname_id,
          kraamnaam:
            apparaat.kraamdeelname_id === null
              ? null
              : (naamPerDeelname.get(apparaat.kraamdeelname_id) ?? null),
          uitgifte,
          vervangingswaardeCent: apparaat.vervangingswaarde_cent,
          huurdagen: huurdagen(uitgifte, nu),
        };
      }),
    );
    setFout(null);
    setLaden(false);
  }, [evenement.id]);

  useEffect(() => {
    void (async () => {
      await ververs();
    })();
  }, [ververs]);

  const geefUit = useCallback(
    async (apparaatId: string, kraamdeelnameId: string) => {
      const { error } = await supabase
        .from('apparaten')
        .update({
          status: 'uitgegeven',
          kraamdeelname_id: kraamdeelnameId,
          uitgegeven_op: new Date().toISOString(),
          retour_op: null,
        })
        .eq('id', apparaatId);
      // Zonder deze controle lijkt een mislukte uitgifte (bijvoorbeeld door
      // RLS) gewoon niets te doen: de knop reageert, het scherm ververst, en
      // niemand ziet waarom het toestel toch op voorraad blijft staan.
      if (error) {
        setFout('Uitgeven lukte niet. Probeer het nog eens.');
        return;
      }
      await ververs();
    },
    [ververs],
  );

  const neemTerug = useCallback(
    async (apparaatId: string) => {
      const { error } = await supabase
        .from('apparaten')
        .update({ status: 'retour', retour_op: new Date().toISOString() })
        .eq('id', apparaatId);
      if (error) {
        setFout('Terugnemen lukte niet. Probeer het nog eens.');
        return;
      }
      await ververs();
    },
    [ververs],
  );

  const zetDefect = useCallback(
    async (apparaatId: string, defect: boolean) => {
      const { error } = await supabase
        .from('apparaten')
        .update({ status: defect ? 'defect' : 'op_voorraad' })
        .eq('id', apparaatId);
      if (error) {
        setFout('Dat kon niet worden bijgewerkt. Probeer het nog eens.');
        return;
      }
      await ververs();
    },
    [ververs],
  );

  return {
    laden,
    fout,
    apparaten,
    overzicht: maakApparaatoverzicht(apparaten, evenement.status === 'afgesloten'),
    kramen,
    ververs,
    geefUit,
    neemTerug,
    zetDefect,
  };
}
